import {Box, Paper, Typography} from "@mui/material";
import {
    Timer as TimerIcon,
    FitnessCenter as SetsIcon,
    MonitorWeight as VolumeIcon,
    TrendingUp as GrowthIcon,
    ArrowUpward,
    ArrowDownward,
} from "@mui/icons-material";
import {colors, borders, shadows} from "../../ui/colors";

const formatDuration = (totalMinutes) => {
    if (!totalMinutes) return "00:00";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const formatNumber = (num) => {
    if (num == null) return "0";
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return Math.round(num).toLocaleString();
};

const StatCard = ({icon: Icon, label, value, diff, unit, iconColor}) => {
    const hasDiff = diff != null && diff !== 0;
    const isPositive = diff > 0;

    return (
        <Paper
            elevation={0}
            sx={{
                flex: 1,
                minWidth: {xs: "140px", md: "0"},
                padding: {xs: "14px 16px", md: "20px 24px"},
                border: borders.default,
                borderRadius: borders.radius.lg,
                boxShadow: shadows.card,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                direction: "rtl",
            }}
        >
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: borders.radius.md,
                        backgroundColor: iconColor || colors.primaryLight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Icon sx={{fontSize: 20, color: iconColor ? colors.white : colors.primary}}/>
                </Box>
                <Typography sx={{fontSize: "12px", fontWeight: 500, color: colors.textMuted}}>
                    {label}
                </Typography>
            </Box>

            <Box sx={{display: "flex", alignItems: "baseline", gap: 0.5}}>
                <Typography sx={{fontSize: "28px", fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2}}>
                    {value}
                </Typography>
                {unit && (
                    <Typography sx={{fontSize: "14px", fontWeight: 500, color: colors.textMuted}}>
                        {unit}
                    </Typography>
                )}
            </Box>

            {hasDiff && (
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    {isPositive ? (
                        <ArrowUpward sx={{fontSize: 14, color: colors.success}}/>
                    ) : (
                        <ArrowDownward sx={{fontSize: 14, color: colors.error}}/>
                    )}
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: isPositive ? colors.success : colors.error,
                        }}
                    >
                        {Math.abs(Math.round(diff))}%
                    </Typography>
                    <Typography sx={{fontSize: "11px", color: colors.textMuted}}>
                        משבוע קודם
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

const WeekOverviewStats = ({statistics, weekFinished = true}) => {
    if (!statistics) return null;

    // Only show diffs when the week is finished — partial data makes comparisons misleading
    const volumeDiff = weekFinished
        ? (statistics.previousWeekVolume && statistics.previousWeekVolume > 0
            ? ((statistics.totalVolume - statistics.previousWeekVolume) / statistics.previousWeekVolume) * 100
            : null)
        : null;

    const setsDiff = weekFinished
        ? (statistics.previousWeekSets && statistics.previousWeekSets > 0
            ? ((statistics.totalNumberOfSets - statistics.previousWeekSets) / statistics.previousWeekSets) * 100
            : null)
        : null;

    return (
        <Box
            sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
            }}
        >
            <StatCard
                icon={TimerIcon}
                label="זמן אימון"
                value={formatDuration(statistics.totalDuration)}
                iconColor={colors.info}
            />
            <StatCard
                icon={SetsIcon}
                label="סה״כ סטים"
                value={statistics.totalNumberOfSets ?? 0}
                diff={setsDiff}
            />
            <StatCard
                icon={VolumeIcon}
                label="נפח אימון"
                value={formatNumber(statistics.totalVolume)}
                unit="ק״ג"
                diff={volumeDiff}
            />
            <StatCard
                icon={GrowthIcon}
                label="צמיחה מתחילת התוכנית"
                value={weekFinished && statistics.volumeGrowthSinceStart != null ? `${Math.round(statistics.volumeGrowthSinceStart)}%` : "—"}
                iconColor={colors.success}
            />
        </Box>
    );
};

export default WeekOverviewStats;
